import React, { useState, useEffect } from "react";
import * as Yup from "yup";
import { Formik, Form, Field } from "formik";
import { toast } from "react-toastify";
import { makeStyles } from "@material-ui/core/styles";
import { green } from "@material-ui/core/colors";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import CircularProgress from "@material-ui/core/CircularProgress";

import Typography from "@material-ui/core/Typography";
import Box from "@material-ui/core/Box";
import Chip from "@material-ui/core/Chip";
import Tooltip from "@material-ui/core/Tooltip";
import AttachFileIcon from "@material-ui/icons/AttachFile";
import { i18n } from "../../translate/i18n";

import ImageIcon from "@material-ui/icons/Image";
import AudiotrackIcon from "@material-ui/icons/Audiotrack";
import VideoLibraryIcon from "@material-ui/icons/VideoLibrary";
import PictureAsPdfIcon from "@material-ui/icons/PictureAsPdf";
import InsertDriveFileIcon from "@material-ui/icons/InsertDriveFile";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";

const useStyles = makeStyles((theme) => ({
    root: { display: "flex", flexWrap: "wrap" },
    multFieldLine: {
        display: "flex",
        "& > *:not(:last-child)": { marginRight: theme.spacing(1) },
    },
    btnWrapper: { position: "relative" },
    buttonProgress: {
        color: green[500],
        position: "absolute",
        top: "50%",
        left: "50%",
        marginTop: -12,
        marginLeft: -12,
    },
    adjuntoSection: {
        marginTop: theme.spacing(2),
        padding: theme.spacing(2),
        border: `1px dashed ${theme.palette.divider}`,
        borderRadius: theme.shape.borderRadius,
        backgroundColor: theme.palette.type === 'dark' ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)',
    },
    adjuntoChip: {
        margin: theme.spacing(0.5),
        maxWidth: 250,
    },
    uploadBtn: {
        marginTop: theme.spacing(1),
    },
    hiddenInput: { display: 'none' },
}));

const getFileIcon = (filename) => {
    const ext = filename.split('.').pop()?.toLowerCase();
    if (['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext)) return <ImageIcon fontSize="small" />;
    if (['mp3', 'ogg', 'wav', 'm4a'].includes(ext)) return <AudiotrackIcon fontSize="small" />;
    if (['mp4', 'mov', 'avi', 'mkv'].includes(ext)) return <VideoLibraryIcon fontSize="small" />;
    if (ext === 'pdf') return <PictureAsPdfIcon fontSize="small" />;
    return <InsertDriveFileIcon fontSize="small" />;
};

const AIPromptKnowledgeDialog = ({ open, onClose, knowledgeId, flowId }) => {
    const classes = useStyles();
    React.useContext(AuthContext);

    const KnowledgeSchema = Yup.object().shape({
        title: Yup.string().required("El título es obligatorio"),
        content: Yup.string().required("El contenido es obligatorio"),
    });

    const initialState = { title: "", content: "", type: "manual" };
    const [knowledge, setKnowledge] = useState(initialState);
    const [adjuntos, setAdjuntos] = useState([]); // array de rutas string
    const [uploading, setUploading] = useState(false);

    useEffect(() => {
        (async () => {
            if (!knowledgeId || !open) return;
            try {
                const { data } = await api.get("/ai-knowledge", { params: { flowId } });
                const item = data.find(i => i.id === knowledgeId);
                if (item) {
                    setKnowledge(item);
                    // Parsear adjunto del DB (JSON array)
                    if (item.adjunto) {
                        try {
                            const parsed = JSON.parse(item.adjunto);
                            setAdjuntos(Array.isArray(parsed) ? parsed : [parsed]);
                        } catch {
                            setAdjuntos([item.adjunto]);
                        }
                    } else {
                        setAdjuntos([]);
                    }
                }
            } catch (err) {
                toastError(err);
            }
        })();
    }, [knowledgeId, open]);

    const handleClose = () => {
        setKnowledge(initialState);
        setAdjuntos([]);
        onClose();
    };

    const handleUploadAdjunto = async (e) => {
        const files = Array.from(e.target.files);
        if (!files.length) return;
        setUploading(true);
        try {
            const formData = new FormData();
            files.forEach(f => formData.append("file", f));
            if (flowId) formData.append("flowId", flowId);
            const { data } = await api.post("/ai-knowledge/upload-adjunto", formData, {
                headers: { "Content-Type": "multipart/form-data" }
            });
            // data.paths = array de rutas subidas
            const newPaths = Array.isArray(data.paths) ? data.paths : [data.path];
            setAdjuntos(prev => [...prev, ...newPaths]);
            toast.success(`${newPaths.length} archivo(s) subido(s)`);
        } catch (err) {
            toastError(err);
        } finally {
            setUploading(false);
            e.target.value = null;
        }
    };

    const handleRemoveAdjunto = (index) => {
        setAdjuntos(prev => prev.filter((_, i) => i !== index));
    };

    const handleSaveKnowledge = async (values) => {
        try {
            const dataToSave = {
                ...values,
                flowId,
                adjunto: adjuntos.length > 0 ? JSON.stringify(adjuntos) : null
            };
            if (knowledgeId) {
                await api.put(`/ai-knowledge/${knowledgeId}`, dataToSave);
            } else {
                await api.post("/ai-knowledge", dataToSave);
            }
            toast.success(i18n.t("common.toasts.success"));
        } catch (err) {
            toastError(err);
        }
        handleClose();
    };

    return (
        <div className={classes.root}>
            <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth scroll="paper">
                <DialogTitle dir={i18n.language === 'ar' ? 'rtl' : 'ltr'}>
                    {knowledgeId ? i18n.t("globalModals.knowledge.edit") : i18n.t("globalModals.knowledge.new")}
                </DialogTitle>
                <Formik
                    initialValues={knowledge}
                    enableReinitialize={true}
                    validationSchema={KnowledgeSchema}
                    onSubmit={(values, actions) => {
                        setTimeout(() => {
                            handleSaveKnowledge(values);
                            actions.setSubmitting(false);
                        }, 400);
                    }}
                >
                    {({ touched, errors, isSubmitting }) => (
                        <Form>
                            <DialogContent dividers dir={i18n.language === 'ar' ? 'rtl' : 'ltr'}>
                                <Field
                                    as={TextField}
                                    label="Título / Nombre del Producto"
                                    name="title"
                                    autoFocus
                                    error={touched.title && Boolean(errors.title)}
                                    helperText={touched.title && errors.title}
                                    variant="outlined"
                                    margin="dense"
                                    fullWidth
                                />
                                <Field
                                    as={TextField}
                                    label="Contenido / Descripción Detallada"
                                    name="content"
                                    error={touched.content && Boolean(errors.content)}
                                    helperText={touched.content && errors.content}
                                    variant="outlined"
                                    margin="dense"
                                    multiline
                                    rows={8}
                                    fullWidth
                                />

                                {/* SECCIÓN DE ADJUNTOS */}
                                <div className={classes.adjuntoSection}>
                                    <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                                        <Typography variant="subtitle2">
                                            <AttachFileIcon fontSize="small" style={{ verticalAlign: 'middle', marginRight: 4 }} />
                                            Archivos Adjuntos ({adjuntos.length})
                                        </Typography>
                                        <Tooltip title="Se enviarán automáticamente cuando el RAG seleccione este ítem">
                                            <Typography variant="caption" color="textSecondary">
                                                Imagen, Audio, Video, PDF, ZIP...
                                            </Typography>
                                        </Tooltip>
                                    </Box>

                                    <Box display="flex" flexWrap="wrap">
                                        {adjuntos.map((ruta, index) => (
                                            <Chip
                                                key={index}
                                                icon={getFileIcon(ruta)}
                                                label={ruta} // [ANTIGRAVITY] FIX: Mostrar ruta completa para mejor referencia
                                                onDelete={() => handleRemoveAdjunto(index)}
                                                className={classes.adjuntoChip}
                                                variant="outlined"
                                                size="small"
                                                title={ruta}
                                            />
                                        ))}
                                        {adjuntos.length === 0 && (
                                            <Typography variant="caption" color="textSecondary">
                                                Sin adjuntos. Agrega las rutas de los archivos (ej. /files/imagen.png).
                                            </Typography>
                                        )}
                                    </Box>

                                    <Box display="flex" alignItems="center" mt={2}>
                                        <TextField
                                            variant="outlined"
                                            size="small"
                                            placeholder="Ingresa la ruta exacta o nombre del archivo"
                                            fullWidth
                                            id="manual-adjunto-input"
                                            helperText="Ejemplo: /files/PLAN_BASIC.png"
                                            onKeyPress={(e) => {
                                                if (e.key === 'Enter') {
                                                    e.preventDefault();
                                                    const val = e.target.value.trim();
                                                    if (val) {
                                                        setAdjuntos(prev => [...prev, val]);
                                                        e.target.value = '';
                                                    }
                                                }
                                            }}
                                        />
                                        <Button
                                            color="primary"
                                            variant="contained"
                                            style={{ marginLeft: 8, height: 40 }}
                                            onClick={() => {
                                                const input = document.getElementById("manual-adjunto-input");
                                                const val = input?.value.trim();
                                                if (val) {
                                                    setAdjuntos(prev => [...prev, val]);
                                                    input.value = '';
                                                }
                                            }}
                                        >
                                            Añadir
                                        </Button>
                                    </Box>
                                </div>
                            </DialogContent>
                            <DialogActions>
                                <Button onClick={handleClose} color="secondary" disabled={isSubmitting} variant="outlined">
                                    {i18n.t("common.cancel")}
                                </Button>
                                <Button
                                    type="submit"
                                    color="primary"
                                    disabled={isSubmitting || uploading}
                                    variant="contained"
                                    className={classes.btnWrapper}
                                >
                                    {knowledgeId ? i18n.t("common.edit") : i18n.t("common.add")}
                                    {isSubmitting && <CircularProgress size={24} className={classes.buttonProgress} />}
                                </Button>
                            </DialogActions>
                        </Form>
                    )}
                </Formik>
            </Dialog>
        </div>
    );
};

export default AIPromptKnowledgeDialog;
